import {
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnChanges,
  output,
} from '@angular/core';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import { ENVIRONMENT_CONFIG, isBrandSegmentType } from '@core/config-angular';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { ContractType } from '@essent/common';
import { QuestionnaireResults, QUESTIONS } from '@retention/shared';
import { ProductDuration } from '@innogy/server/get-offer/interfaces';
import { AnalyticsActionTypes } from '@analytics/models';
import { mapOfferToCommerce } from '@innogy/become-a-customer/shared';
import { TrackProductService, TrackStepDirective } from '@analytics/events';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CSCaptureDirective } from '@legacy/utils-contentsquare';

import {
  CommodityOfferAlignment,
  CommodityOfferContent,
  type CommodityOfferLayoutType,
} from '../commodity-offers.model';
import { getSpecificOfferValueFromOffer } from '../utils';
import { VariableOfferCardComponent } from '../variable-offer-card/variable-offer-card.component';
import { EssentConsumerRetentionMinimalCommodityOfferComponent } from '../commodity-offer-brand/essent-consumer-retention-minimal-commodity-offer/essent-consumer-retention-minimal-commodity-offer.component';

@Component({
  selector: 'wl-commodity-best-offer-list',
  templateUrl: './commodity-best-offer-list.component.html',
  styleUrls: [
    '../commodity-offer-list/commodity-offer-list.component.essent.scss',
    './commodity-best-offer-list.component.scss',
  ],
  standalone: true,
  imports: [
    TrackStepDirective,
    AutomationIdDirective,
    CSCaptureDirective,
    VariableOfferCardComponent,
    EssentConsumerRetentionMinimalCommodityOfferComponent,
  ],
})
export class CommodityBestOfferListComponent implements OnChanges {
  // offers input normally does not contain a variable offer, so we need to append it
  readonly offers = input<Offer[]>();
  readonly content = input<CommodityOfferContent>();
  readonly showMemberGetMember = input(false, { transform: booleanAttribute });
  readonly alignment = input<CommodityOfferAlignment>();
  readonly questionnaireResults = input<QuestionnaireResults>();

  nrOfQuestions = QUESTIONS.length ?? 0;

  selectOffer = output<Offer>();

  readonly answers = computed(() =>
    Object.assign(
      {},
      ...(this.questionnaireResults()?.answers?.map((answer) => {
        if (typeof answer.value === 'string') {
          return {
            [answer.questionKey]: answer.value,
          };
        } else {
          return {
            [answer.questionKey]: this.concatMultipartQuestionAnswers(
              answer.value as Record<string, boolean>
            ),
          };
        }
      }) ?? [])
    )
  );

  concatMultipartQuestionAnswers(multipartQuestion: Record<string, boolean>) {
    return Object.entries(multipartQuestion)
      .map(([k, v]) => (v ? k : undefined))
      .filter((v) => v !== undefined)
      .join(', ');
  }

  selectAndTrackOffer(offer: Offer) {
    this.selectOffer.emit(offer);

    const match = getSpecificOfferValueFromOffer(offer);
    const commerceObject = {
      ...mapOfferToCommerce(offer),
      isBesteKeuze: !!match && this.isHighestMatch(match),
    };

    this.trackProductService.trackSelection(commerceObject);
  }

  openMoreInfo = output<Offer>();
  viewTariffs = output<Offer>();
  showExplanation = output<ReturnType<typeof getSpecificOfferValueFromOffer>>();
  selectVariableOffer = output();

  computedVariant?: CommodityOfferLayoutType;
  selectedContractType?: ContractType;

  private readonly envConfig = inject(ENVIRONMENT_CONFIG);
  private readonly mediaObserverService = inject(MediaObserverService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly trackProductService = inject(TrackProductService);

  readonly variableOfferEntry: Offer = {
    contractType: 'variable',
    offerId: '',
    campaignId: '',
    duration: ProductDuration.ONE,
    commodities: [],
    offerOverviews: [],
    vatIncluded: false,
    expectedYearlyAmount: 0,
    expectedMonthlyAmount: 0,
    beforeDiscountExpectedYearlyAmount: 0,
    beforeDiscountExpectedMonthlyAmount: 0,
    usps: [],
    disclaimerContent: '',
    isDynamicProduct: false,
    productTitle: this.content()?.questionnaireVariableCardTitle ?? 'Variabel',
    productSubtitle: '',
    isHighlighted: false,
    dynamicPricingTitle: '',
  };

  readonly isMijnConsumentEssent = computed(() =>
    isBrandSegmentType({
      brand: 'essent',
      segment: 'consument',
      type: 'mijn',
      currentBrand: this.envConfig.brand,
      currentSegment: this.envConfig.segment,
      currentType: this.envConfig.type,
    })
  );
  readonly offersExist = computed(
    () => this.offers() && (this.offers() as Offer[]).length > 0
  );

  readonly listContainsHighlights = computed(
    () =>
      !!this.offers()?.some(
        (offer) => offer.isHighlighted && offer.isHighlightedLabel
      )
  );

  readonly listContainsIncentives = computed(
    () => !!this.offers()?.some((offer) => offer.incentiveId)
  );

  readonly initiallyActiveOfferIds = computed(() => [
    (this.offers() ?? [])[0].offerId,
  ]);

  isHighestMatch = (match: QuestionnaireResults['highestMatch']) =>
    this.questionnaireResults()?.highestMatch === match;

  readonly matchingOffer = computed(() => {
    if (this.isHighestMatch('variable')) {
      const variableOffer: { contractType: Offer['contractType'] } = {
        contractType: 'variable',
      };
      return variableOffer;
    }

    return this.offers()
      ?.map((offer) => ({ ...offer, usps: [], isHighlightedLabel: undefined }))
      ?.find((offer) => {
        const highestMatch = this.questionnaireResults()?.highestMatch;
        const contractType = offer.contractType;

        switch (highestMatch) {
          case 'dynamic': {
            return contractType === 'dynamic';
          }
          case 'variable': {
            // variable case should normally not be part of the offer list, but added here regardless
            return contractType === 'variable';
          }
          case 'fixed1Year': {
            return (
              contractType === 'fixed' && offer.duration === ProductDuration.ONE
            );
          }
          case 'fixed3Years': {
            return (
              contractType === 'fixed' &&
              offer.duration === ProductDuration.THREE
            );
          }
          default:
            return false;
        }
      });
  });

  readonly remainingOffers = computed<Offer[]>(() => {
    const matchingOffer = this.matchingOffer();
    const offers = [...(this.offers() ?? [])];

    if (!this.isHighestMatch('variable')) {
      offers.push(this.variableOfferEntry);
    }

    const unsortedOffers: (Offer & { matchingValue?: number })[] =
      matchingOffer &&
      matchingOffer.contractType !== 'variable' &&
      'offerId' in matchingOffer
        ? offers.filter((offer) => offer.offerId !== matchingOffer.offerId)
        : offers;

    const unsortedOffersWithMatchingValue = unsortedOffers.map((offer) => {
      let matchingValue: number | undefined;
      switch (offer.contractType) {
        case 'dynamic':
          matchingValue = this.questionnaireResults()?.dynamic;
          break;
        case 'variable':
          matchingValue = this.questionnaireResults()?.variable;
          break;
        case 'fixed': {
          if (offer.duration === ProductDuration.ONE) {
            matchingValue = this.questionnaireResults()?.fixed1Year;
          } else if (offer.duration === ProductDuration.THREE) {
            matchingValue = this.questionnaireResults()?.fixed3Years;
          }
          break;
        }
        default:
          break;
      }

      return {
        ...offer,
        usps: [],
        incentiveId: undefined,
        isHighlightedLabel: undefined,
        matchingValue,
      };
    });

    return unsortedOffersWithMatchingValue.sort((a, b) =>
      a.matchingValue && b.matchingValue ? b.matchingValue - a.matchingValue : 0
    );
  });

  constructor() {
    effect(() => {
      const isDesktop = this.mediaObserverService.deviceInfo().isDesktop;
      if (this.alignment() === 'vertical' || !isDesktop) {
        this.computedVariant = 'accordion';
      } else {
        this.computedVariant = 'card';
      }

      // Manually trigger change detection
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnChanges() {
    this.offers()?.forEach((offer) => {
      const match = getSpecificOfferValueFromOffer(offer);
      const commerceObject = {
        ...mapOfferToCommerce(offer),
        isBesteKeuze: !!match && this.isHighestMatch(match),
      };

      this.trackProductService.trackImpression(commerceObject);
    });
    if (!this.offers()?.some((offer) => offer.contractType === 'variable')) {
      const commerceObject = {
        ...mapOfferToCommerce(this.variableOfferEntry),
        isBesteKeuze: this.isHighestMatch('variable'),
      };
      this.trackProductService.trackImpression(commerceObject);
    }
  }

  protected readonly getSpecificOfferValueFromOffer =
    getSpecificOfferValueFromOffer;
  protected readonly AnalyticsActionTypes = AnalyticsActionTypes;
}
